import i18n, { type AppLanguage, DEFAULT_LANGUAGE } from '@repo/i18n'
import { endOfDay, isToday, isYesterday, startOfDay, subDays } from 'date-fns'
import { enUS, it, type Locale } from 'date-fns/locale'
import { format, formatInTimeZone } from 'date-fns-tz'
import type { DateRange } from 'react-day-picker'
import { z } from 'zod'

const locales = {
	en: enUS,
	it
} as const satisfies Record<AppLanguage['code'], Locale>

export const zDateSearchParam = z
	.union([
		z.date(),
		// A date-time without offset parses as local time, matching the
		// calendar-day intent of a plain date param.
		z.iso.date().transform((value) => new Date(`${value}T00:00:00`)),
		z.iso
			.datetime({ local: true, offset: true })
			.transform((value) => new Date(value))
	])
	.optional()
	.catch(undefined)

export const formatLocaleDate = (date: string | Date, formatType = 'P') => {
	const currentLang = i18n.language || DEFAULT_LANGUAGE.code
	const locale =
		locales[currentLang as keyof typeof locales] ||
		locales[DEFAULT_LANGUAGE.code]
	return format(new Date(date), formatType, { locale })
}

export const getTimezone = () => {
	return Intl.DateTimeFormat().resolvedOptions().timeZone
}

export const formatInTimezone = (date: string | Date, formatType = 'P') => {
	const timezone = getTimezone()
	return formatInTimeZone(new Date(date), timezone, formatType, {
		locale:
			locales[i18n.language as keyof typeof locales] ||
			locales[DEFAULT_LANGUAGE.code]
	})
}

export function formatRecentDate(
	date: string | Date,
	options: {
		yesterday: string
		todayFormat?: string
		weekFormat?: string
		fallbackFormat?: string
	}
) {
	const value = new Date(date)

	if (isToday(value)) {
		return formatLocaleDate(value, options.todayFormat ?? 'p')
	}

	if (isYesterday(value)) {
		return options.yesterday
	}

	if (value > subDays(new Date(), 7)) {
		return formatLocaleDate(value, options.weekFormat ?? 'EEEE')
	}

	return formatLocaleDate(value, options.fallbackFormat ?? 'P')
}

export function dateToStartOfDayIso(date: Date | undefined) {
	return date ? startOfDay(date).toISOString() : undefined
}

export function dateToEndOfDayIso(date: Date | undefined) {
	return date ? endOfDay(date).toISOString() : undefined
}

export function toDateRange(
	from: Date | undefined,
	to: Date | undefined
): DateRange | undefined {
	return from || to ? { from, to } : undefined
}

export function getLocalCalendarDateKey(date: string | Date) {
	const value = new Date(date)
	return (
		value.getFullYear() * 10_000 + value.getMonth() * 100 + value.getDate()
	)
}

const ITALY_TIME_ZONE = 'Europe/Rome'

/**
 * A date or time as it reads in Italy, in the given language: the same for a
 * visitor anywhere and for the server that renders the page, which knows no
 * time zone of the reader.
 */
export const formatItalianTime = (
	date: string | Date,
	formatType: string,
	language: AppLanguage['code']
) =>
	formatInTimeZone(new Date(date), ITALY_TIME_ZONE, formatType, {
		locale: locales[language]
	})
